import * as React from "react";
import Container from "@amzn/awsui-components-react/polaris/container";
import Header from "@amzn/awsui-components-react/polaris/header";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import Input from "@amzn/awsui-components-react/polaris/input";
import Textarea from "@amzn/awsui-components-react/polaris/textarea";
import Button from "@amzn/awsui-components-react/polaris/button";
import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import ColumnLayout from "@amzn/awsui-components-react/polaris/column-layout";
import Flashbar, { FlashbarProps } from "@amzn/awsui-components-react/polaris/flashbar";
import Box from "@amzn/awsui-components-react/polaris/box";
import { API } from "aws-amplify";

// requestedBy is NOT collected here: the Lambda supplies the shared PillPack admin actor, as the
// CCS decision flow does. The operator only provides the fill, profile, and an optional reason.
// The op is the generic ExecuteVerification; this screen posts the copay-waiver intent/detail.
interface ExecuteVerificationResponse {
  verificationExecutionId?: string;
  executionStatus?: string;
  verificationResult?: {
    outcomeCode?: string;
    resultDetail?: {
      hardshipWaiver?: {
        rejectionReasons?: string[];
        ineligibilityReasons?: string[];
      };
    };
  };
}

// Named constants in place of hard-coded strings for the API surface + CLIVE intent/outcome literals.
const API_NAME = "ApaqPortalAPI";
const EXECUTE_VERIFICATION_PATH = "/clinical-verification/execute-verification";
const VERIFICATION_INTENT = "COPAY_HARDSHIP_WAIVER";
const ENTITY_TYPE_FILL_ID = "FILL_ID";
const EXECUTION_STATUS = { COMPLETED: "COMPLETED", INELIGIBLE: "INELIGIBLE" } as const;
const OUTCOME_CODE = { APPROVED: "APPROVED", REJECTED: "REJECTED" } as const;

const RESPONSE_PRE_STYLE: React.CSSProperties = {
  backgroundColor: "#f5f5f5",
  border: "1px solid lightgray",
  borderRadius: "4px",
  padding: "10px",
  overflowX: "auto",
  whiteSpace: "pre-wrap",
  wordBreak: "normal",
};

interface State {
  patientHealthProfileId: string;
  fillId: string;
  waiverReason: string;
  isSubmitting: boolean;
  response: ExecuteVerificationResponse | null;
  submittedFillId: string | null;
  flashMessages: FlashbarProps.MessageDefinition[];
}

class CopayHardshipWaiverSection extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      patientHealthProfileId: "",
      fillId: "",
      waiverReason: "",
      isSubmitting: false,
      response: null,
      submittedFillId: null,
      flashMessages: [],
    };
  }

  // Clears any prior result/flash so a stale outcome can never render beside freshly edited inputs.
  private onFieldChange = (field: "patientHealthProfileId" | "fillId" | "waiverReason", value: string) => {
    this.setState({ [field]: value, response: null, submittedFillId: null, flashMessages: [] } as unknown as Pick<State, keyof State>);
  };

  // The waiver auto-resolves synchronously, so the single response carries the terminal outcome.
  private outcomeFlash(r: ExecuteVerificationResponse, fillId: string): FlashbarProps.MessageDefinition {
    const dismiss = () => this.setState({ flashMessages: [] });
    const waiver = r.verificationResult?.resultDetail?.hardshipWaiver;
    const reasonList = waiver?.rejectionReasons?.length ? waiver.rejectionReasons : waiver?.ineligibilityReasons;
    const reasons = (reasonList || []).join(", ");
    const outcomeCode = r.verificationResult?.outcomeCode;
    if (r.executionStatus === EXECUTION_STATUS.COMPLETED && outcomeCode === OUTCOME_CODE.APPROVED) {
      return { type: "success", content: `Waiver approved — the copay for fill ${fillId} has been waived.`, dismissible: true, onDismiss: dismiss };
    }
    if (r.executionStatus === EXECUTION_STATUS.COMPLETED && outcomeCode === OUTCOME_CODE.REJECTED) {
      return { type: "warning", content: `Waiver rejected for fill ${fillId}${reasons ? `: ${reasons}` : "."}`, dismissible: true, onDismiss: dismiss };
    }
    if (r.executionStatus === EXECUTION_STATUS.INELIGIBLE) {
      return { type: "info", content: `Waiver ineligible for fill ${fillId}${reasons ? `: ${reasons}` : "."}`, dismissible: true, onDismiss: dismiss };
    }
    return { type: "info", content: `Execution status for fill ${fillId}: ${r.executionStatus || "unknown"}`, dismissible: true, onDismiss: dismiss };
  }

  private handleSubmit = async () => {
    if (this.state.isSubmitting) return;

    const { patientHealthProfileId, fillId, waiverReason } = this.state;
    if (!patientHealthProfileId.trim() || !fillId.trim()) {
      this.setState({
        flashMessages: [{ type: "error", content: "Profile ID and Fill ID are both required.", dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
      return;
    }

    this.setState({ isSubmitting: true, flashMessages: [], response: null, submittedFillId: null });

    const submittedFillId = fillId.trim();
    try {
      const hardshipWaiver: any = { patientHealthProfileId: patientHealthProfileId.trim() };
      if (waiverReason.trim()) {
        hardshipWaiver.waiverReason = waiverReason.trim();
      }
      const body = {
        targetEntity: { id: submittedFillId, type: ENTITY_TYPE_FILL_ID },
        verificationIntent: VERIFICATION_INTENT,
        verificationRequestDetails: { hardshipWaiver },
      };

      const response = await API.post(API_NAME, EXECUTE_VERIFICATION_PATH, { body });

      this.setState({ isSubmitting: false, response, submittedFillId, flashMessages: [this.outcomeFlash(response, submittedFillId)] });
    } catch (e) {
      const err = e as any;
      const message = err?.response?.data?.message || err?.message || "Failed to execute copay hardship waiver";
      this.setState({
        isSubmitting: false,
        flashMessages: [{ type: "error", content: message, dismissible: true, onDismiss: () => this.setState({ flashMessages: [] }) }],
      });
    }
  };

  render() {
    const { patientHealthProfileId, fillId, waiverReason, isSubmitting, response, submittedFillId, flashMessages } = this.state;
    const canSubmit = patientHealthProfileId.trim() && fillId.trim();

    return (
      <SpaceBetween size="l">
        {flashMessages.length > 0 && <Flashbar items={flashMessages} />}

        <Container header={
          <Header
            variant="h3"
            actions={
              <Button variant="primary" onClick={this.handleSubmit} loading={isSubmitting} disabled={!canSubmit}>
                Submit Waiver
              </Button>
            }
          >
            Medicaid Hardship Copay Waiver
          </Header>
        }>
          <SpaceBetween size="l">
            <ColumnLayout columns={2}>
              <FormField label="Profile ID" description="Required — patient health profile">
                <Input
                  value={patientHealthProfileId}
                  onChange={({ detail }) => this.onFieldChange("patientHealthProfileId", detail.value)}
                  disabled={isSubmitting}
                  placeholder="amzn1.pharmacy.profile.id...."
                />
              </FormField>
              <FormField label="Fill ID" description="Required — the PENDING fill to waive">
                <Input
                  value={fillId}
                  onChange={({ detail }) => this.onFieldChange("fillId", detail.value)}
                  disabled={isSubmitting}
                  placeholder="fill-id"
                />
              </FormField>
            </ColumnLayout>

            <FormField label="Reason" description="Optional — audit justification">
              <Textarea
                value={waiverReason}
                onChange={({ detail }) => this.onFieldChange("waiverReason", detail.value)}
                disabled={isSubmitting}
                placeholder="Patient reported inability to pay the copay for this fill."
                rows={3}
              />
            </FormField>
          </SpaceBetween>
        </Container>

        {response && (
          <Container header={<Header variant="h3">{`Response — fill ${submittedFillId}`}</Header>}>
            <Box>
              <pre style={RESPONSE_PRE_STYLE}>{JSON.stringify(response, null, 2)}</pre>
            </Box>
          </Container>
        )}
      </SpaceBetween>
    );
  }
}

export default CopayHardshipWaiverSection;
